import React from 'react'

import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import Dropdown, { type DropdownOpenDirection } from '../ui/dropdown'
import InvoiceActionsDropdown from './invoiceActionsDropdown'
import type { InvoiceFormActionId, InvoiceFormActionSurfaces } from './invoiceFormActionSurfaces'
import { InvoiceActivity, InvoiceStatus } from '../../types'
import { ReactComponent as SaveIcon } from '../../static/images/save.svg'
import { ReactComponent as MoneyIcon } from '../../static/images/money.svg'

export type InvoiceFormActionsLayout = 'header' | 'footer' | 'bottomBar'

type InvoiceActionsTarget = {
  _id?: string
  status?: InvoiceStatus
  total?: number | string
  lastActivity?: InvoiceActivity
  sentManually?: boolean
}

export type InvoiceFormActionsProps = {
  actions: InvoiceFormActionId[]
  layout: InvoiceFormActionsLayout
  surfaces: Pick<InvoiceFormActionSurfaces, 'sendLabel' | 'viewPdfLabel' | 'menuItems'>
  formId: string
  isNewInvoice: boolean
  invoice: InvoiceActionsTarget
  saveDisabled: boolean
  isSavingDraft: boolean
  isLoading: boolean
  isUpdatingBusinessDetails: boolean
  isSendingToEmail: boolean
  isPreparingPdf: boolean
  /** Header/bottom bar: disables send when invoice is already sent (non-overdue). Footer omits this. */
  sendPolicyDisabled: boolean
  /** Footer only: disables send for overdue zero-total invoices. */
  sendReminderDisabled: boolean
  onSend: (e: React.MouseEvent) => void
  onViewPdf: (e: React.MouseEvent) => void
  onClearForm: () => void
  onDuplicate: (e: React.MouseEvent) => void
  onDelete?: (e: React.MouseEvent) => void
  className?: string
}

const layoutClassName = (layout: InvoiceFormActionsLayout, className?: string): string => {
  const base = layout === 'footer'
    ? 'invoice-actions-flex mt-4 justify-content-end'
    : layout === 'bottomBar'
      ? 'invoice-actions-flex invoice-actions-flex--bottom-bar'
      : 'invoice-actions-flex invoice-actions-flex--header-mobile-row'
  return className ? `${base} ${className}` : base
}

const isSendDisabled = (
  layout: InvoiceFormActionsLayout,
  props: Pick<
    InvoiceFormActionsProps,
    | 'isSendingToEmail'
    | 'isLoading'
    | 'isUpdatingBusinessDetails'
    | 'sendPolicyDisabled'
    | 'sendReminderDisabled'
  >,
): boolean => {
  const { isSendingToEmail, isLoading, isUpdatingBusinessDetails, sendPolicyDisabled, sendReminderDisabled } = props
  if (isSendingToEmail || isLoading || isUpdatingBusinessDetails) {
    return true
  }
  if (layout === 'footer') {
    return sendReminderDisabled
  }
  return sendPolicyDisabled
}

const InvoiceFormActions: React.FC<InvoiceFormActionsProps> = ({
  actions,
  layout,
  surfaces,
  formId,
  isNewInvoice,
  invoice,
  saveDisabled,
  isSavingDraft,
  isLoading,
  isUpdatingBusinessDetails,
  isSendingToEmail,
  isPreparingPdf,
  sendPolicyDisabled,
  sendReminderDisabled,
  onSend,
  onViewPdf,
  onClearForm,
  onDuplicate,
  onDelete,
  className,
}) => {
  const saveLoading = isSavingDraft || isLoading || isUpdatingBusinessDetails
  const sendDisabled = isSendDisabled(layout, {
    isSendingToEmail,
    isLoading,
    isUpdatingBusinessDetails,
    sendPolicyDisabled,
    sendReminderDisabled,
  })
  const isCompact = layout === 'header' || layout === 'bottomBar'
  const menuOpenDirection: DropdownOpenDirection = layout === 'bottomBar' ? 'up' : 'down'

  const renderSave = () => (
    <div
      key='save'
      className={`invoice-actions-item${isCompact ? ' invoice-actions-item--compact' : ''}`}
    >
      <Button
        type='submit'
        form={formId}
        size={ButtonSize.DEFAULT}
        variant={ButtonVariant.LIGHT}
        icon={<SaveIcon width={24} />}
        iconPosition='start'
        loading={saveLoading}
        loadingText='Saving...'
        disabled={saveDisabled || isSavingDraft}
      >
        Save
      </Button>
    </div>
  )

  const renderSend = () => (
    <div
      key='send'
      className={`invoice-actions-item${isCompact ? ' invoice-actions-item--compact' : ''}`}
    >
      <Button
        type='button'
        size={ButtonSize.DEFAULT}
        variant={ButtonVariant.PRIMARY}
        loading={isSendingToEmail}
        loadingText='Saving...'
        disabled={sendDisabled}
        onClick={onSend}
      >
        {surfaces.sendLabel}
      </Button>
    </div>
  )

  const renderViewPdf = () => (
    <div key='viewPdf' className='invoice-actions-item'>
      <Button
        type='button'
        variant={ButtonVariant.PLAIN}
        icon={<MoneyIcon width={24} />}
        iconPosition='start'
        size={ButtonSize.DEFAULT}
        onClick={onViewPdf}
        loading={isPreparingPdf}
        loadingText='Preparing Invoice'
        visibleLoadingText
        disabled={isPreparingPdf || isLoading || isUpdatingBusinessDetails}
      >
        View PDF
      </Button>
    </div>
  )

  const renderMenu = () => {
    if (isNewInvoice) {
      if (!surfaces.menuItems.includes('clearForm')) {
        return null
      }
      return (
        <div key='menu' className='invoice-actions-item invoice-actions-item--icon-only'>
          <Dropdown buttonLabel='' openDirection={menuOpenDirection}>
            <button type='button' onClick={onClearForm} disabled={isLoading}>
              Clear Form
            </button>
          </Dropdown>
        </div>
      )
    }

    return (
      <InvoiceActionsDropdown
        key='menu'
        invoice={{
          _id: invoice._id,
          status: invoice.status,
          total: invoice.total,
          lastActivity: invoice.lastActivity,
          sentManually: invoice.sentManually,
        }}
        disabled={isLoading}
        showSendReminder={false}
        showSendInMenu={layout !== 'bottomBar'}
        showDelete={!!onDelete}
        viewPdfLabel={surfaces.viewPdfLabel}
        viewPdfDisabled={isPreparingPdf}
        sendDisabled={isSendingToEmail}
        onViewPdf={onViewPdf}
        onSendToClient={onSend}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        openDirection={menuOpenDirection}
      />
    )
  }

  const renderAction = (actionId: InvoiceFormActionId): React.ReactNode => {
    switch (actionId) {
      case 'save':
        return renderSave()
      case 'send':
        return renderSend()
      case 'viewPdf':
        return renderViewPdf()
      case 'menu':
        return renderMenu()
      default:
        return null
    }
  }

  if (actions.length === 0) {
    return null
  }

  return (
    <div className={layoutClassName(layout, className)}>
      {actions.map(renderAction)}
    </div>
  )
}

export default InvoiceFormActions
